import axios from 'axios';
import _ from 'lodash';
import {useCallback, useContext, useEffect, useReducer, useState} from 'react';

import SignalRContext from '../signalrcontext';
import {DataFilter} from './data-filter';
import {getDataEndpointUrl} from './data-url-helpers';
import {buildFetchEntityListOptions, fetchEntityList} from './entity-list-v2';

// Actions
const LOADED_DETAIL = 'simba/data/LOADED_DETAIL';
const LOADING_DETAIL = 'simba/data/LOADING_DETAIL';
const ERROR_LOADING_DETAIL = 'simba/data/ERROR_LOADING_DETAIL';
const ENTITY_PATCH = 'simba/data/ENTITY_PATCH';

const initialState = {
    entityDetail: null,
    loading: null,
    error: null,
    etag: null,
};

function getAxiosError(err) {
    if (_.get(err, 'response.status') === 404) {
        return 'errors.notfound';
    }
    if (_.get(err, 'response.status') === 401) {
        return 'errors.unauthorized';
    }
    if (_.get(err, 'response.status') === 403) {
        return 'errors.forbid';
    }
    if (_.get(err, 'response.status') === 410) {
        return 'errors.entitygone';
    }
    return 'errors.general';
}

function reducer(state, action) {
    switch (action.type) {
        case LOADING_DETAIL:
            return {
                ...state,
                loading: _.get(state, 'loading', 0) + 1,
            };
        case LOADED_DETAIL:
            return {
                ...state,
                loading: _.get(state, 'loading', 0) - 1,
                entityDetail: action.payload,
                etag: action.etag,
                error: null,
            };
        case ERROR_LOADING_DETAIL:
            return {
                ...state,
                loading: _.get(state, 'loading', 0) - 1,
                entityDetail: null,
                etag: null,
                error: getAxiosError(action.payload),
            };
        case ENTITY_PATCH:
            return {
                ...state,
                entityDetail: state.entityDetail == null ? null : {...state.entityDetail, ...action.payload},
            };
        default:
            return state;
    }
}

export function loadingEntity() {
    return {
        type: LOADING_DETAIL,
    };
}

export function loadedEntity(response, etag) {
    return {
        type: LOADED_DETAIL,
        payload: response,
        etag: etag,
    };
}

export function errorLoadingEntity(err) {
    return {
        type: ERROR_LOADING_DETAIL,
        payload: err,
    };
}

export function patchEntity(payload) {
    return {
        type: ENTITY_PATCH,
        payload: payload,
    };
}

export function loadEntity(dispatch, entityTypeIdOrName, entityId, joinStr, project) {
    if (!entityId) return null;

    dispatch(loadingEntity());

    const endpoint = getDataEndpointUrl(entityTypeIdOrName);

    return axios
        .get(`${endpoint}/${entityId}`, {params: {join: joinStr, project}})
        .then((response) => {
            let etag = _.get(response.headers, 'etag', null);
            return dispatch(loadedEntity(response.data, etag));
        })
        .catch((err) => {
            return dispatch(errorLoadingEntity(err));
        });
}

/**
 * Loads a single entity with the specified `entityId` that is of the specified `entityTypeIdOrName`.
 *
 * @param {string | number} entityTypeIdOrName
 * The ID (`number`) or name (`string`) of the entity type to load.
 * *💡 Tip:* Prefer using the name over the ID, so that your code is not dependent on the Tarasa environment.
 *
 * @param {string} entityId
 * The ID of the entity to load.
 *
 * @param {object} [params]
 * Additional parameters to pass to the API.
 *
 * @param {string} [params.project]
 * The projection to apply when loading entities.
 *
 * @param {string} [params.join]
 * The join to apply when loading entities.
 *
 * *💡 Tip:* Use `project` instead to load only relevant fields, reduce the amount of data transferred, and improve performance.
 */
export function loadEntityRaw(entityTypeIdOrName, entityId, params) {
    if (!entityId) return null;

    const options = buildFetchEntityListOptions({
        join: _.get(params, 'join'),
        project: _.get(params, 'project'),
        filter: DataFilter.eq('entityId', entityId),
    });

    return fetchEntityList(entityTypeIdOrName, options).then((data) => _.head(data.entities));
}

/**
 * The `useEntityDetail` hook provides a way to load a single entity of the specified `entityTypeIdOrName` with the specified `entityId`.
 * The hook returns the entity data, along with metadata about it.
 *
 * @param {number | string} entityTypeIdOrName
 * The id (number) or name (string) of the entity data type.
 *
 * @param {string} entityId
 * The id of the entity.
 *
 * @param {string} [joinStr]
 * The join to apply when loading the entity.
 *
 * **💡 Tip:** Use `project` instead to load only relevant fields, reduce the amount of data transferred, and improve performance.
 *
 * @param {string} [project]
 * The projection to apply when loading entities.
 *
 * @returns {UseEntityDetailResult}
 */
export function useEntityDetail(entityTypeIdOrName, entityId, joinStr, project) {
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        if (entityId !== null) {
            loadEntity(dispatch, entityTypeIdOrName, entityId, joinStr, project);
        } else {
            dispatch(loadingEntity());
            dispatch(loadedEntity(null, null));
        }
    }, [entityTypeIdOrName, entityId, joinStr, project]);

    const reload = useCallback(
        () => loadEntity(dispatch, entityTypeIdOrName, entityId, joinStr, project),
        [entityTypeIdOrName, entityId, joinStr, project],
    );
    const patch = useCallback((payload) => dispatch(patchEntity(payload)), []);

    return {
        entityDetail: state.entityDetail,
        etag: state.etag,
        loading: state.loading,
        error: state.error,
        reload,
        patch,
    };
}

export function useEntityListener(entityTypeIdOrName, entityIds) {
    const [reloadNeeded, setReloadNeeded] = useState(false);
    const connection = useContext(SignalRContext);

    // when entityDetail changes, the callback needs to change too, because it captures it
    const entityChanged = useCallback(
        (data) => {
            if (_.includes(entityIds, data.entityId)) {
                setReloadNeeded(true);
            }
        },
        [entityIds],
    );

    // listen to the signalR events
    useEffect(() => {
        if (!connection) return;

        connection.invoke('listenToEntities', entityIds);
        return () => {
            connection.invoke('stopListenToEntities', entityIds);
        };
    }, [connection, entityIds]);

    // register to the entityChanged events, whenever the callback changes
    useEffect(() => {
        if (!connection) return;

        connection.on('entityChanged', entityChanged);
        return () => {
            connection.off('entityChanged', entityChanged);
        };
    }, [connection, entityChanged]);

    const doReload = () => {
        setReloadNeeded(false);
    };

    return {
        reloadNeeded,
        setReloaded: doReload,
    };
}

/**
 * The `useLiveEntityDetail` hook provides a way to load a single entity of the specified `entityTypeIdOrName` with the specified `entityId`.
 * Additionally, it provides the `reloadNeeded` property, which indicates whether the entity data has been updated from a different source.
 * The hook returns the entity data, along with metadata about it.
 *
 * @param {number | string} entityTypeIdOrName
 * The id (number) or name (string) of the entity data type.
 *
 * @param {string} entityId
 * The id of the entity.
 *
 * @param {string} [joinStr]
 * The join to apply when loading the entity.
 *
 * **💡 Tip:** Use `project` instead to load only relevant fields, reduce the amount of data transferred, and improve performance.
 *
 * @param {string} [project]
 * The projection to apply when loading entities.
 *
 * @returns {UseLiveEntityDetailResult}
 */
export function useLiveEntityDetail(entityTypeIdOrName, entityId, joinStr, project) {
    const entityDetail = useEntityDetail(entityTypeIdOrName, entityId, joinStr, project);
    const [reloadNeeded, setReloadNeeded] = useState(false);
    const connection = useContext(SignalRContext);

    // when entityDetail changes, the callback needs to change too, because it captures it
    const entityChanged = useCallback(
        (data) => {
            if (data.entityId == entityId) {
                if (data.requireReload && entityDetail.etag != null && data.documentId != entityDetail.etag) {
                    setReloadNeeded(true);
                } else {
                    entityDetail.patch(data.payload);
                }
            }
        },
        [entityDetail, joinStr],
    );

    // listen to the signalR events
    useEffect(() => {
        // TODO: Remove this _.isNil() check when proper eTag handling is supported for change tracking.
        if (!connection || _.isNil(entityId)) return;

        connection.invoke('listenToEntity', entityId);
        return () => {
            connection.invoke('stopListenToEntity', entityId);
        };
    }, [connection, entityId]);

    // register to the entityChanged events, whenever the callback changes
    useEffect(() => {
        if (!connection) return;

        connection.on('entityChanged', entityChanged);
        return () => {
            connection.off('entityChanged', entityChanged);
        };
    }, [connection, entityChanged]);

    const doReload = () => {
        entityDetail.reload();
        setReloadNeeded(false);
    };

    return {
        ...entityDetail,
        reloadNeeded,
        reload: doReload,
    };
}

/**
 * @typedef {object} UseEntityDetailResult
 *
 * @property {object} entityDetail
 * The entity data.
 *
 * @property {string} etag
 * The latest etag of the entity.
 *
 * @property {boolean} loading
 * Whether the entity is currently being loaded.
 *
 * @property {import('axios').AxiosError | null} error
 * The error that occurred while loading the entity.
 *
 * @property {() => Promise} reload
 * Reloads the entity data.
 *
 * @property {(payload: object) => Promise} patch
 * Patches the _loaded_ entity data with the specified payload. **⚠️ NOTE:** This does **not** save the entity data to the server.
 */

/**
 * @typedef {object} UseLiveEntityDetailResultProperties
 *
 * @property {boolean} reloadNeeded
 * Indicates whether the entity data has been updated from a different source.
 *
 * @typedef {UseEntityDetailResult & UseLiveEntityDetailResultProperties} UseLiveEntityDetailResult
 */
